// The save store (decision 0033): manual saves into numbered slots, an autosave the game calls at checkpoints and
// zone changes (throttled), and small named values such as settings, all through one backend. Documents go through
// the game's schema on the way in and out (versioned slices, migrations) and through the compressed container.
// The store never decides when to save: the game calls `autosave` from its own events.
import { decodeSave, encodeSave, fromJsonBytes, jsonBytes, type Compression, type EncodeStats } from "./codec";
import { infoOf, revOf, type RecordInfo, type SaveBackend } from "./backend";
import type { ReadResult, SaveMeta, SaveSchema } from "./schema";

/** Options for {@link createSaveStore}. */
export interface SaveStoreOptions {
  schema: SaveSchema;
  backend: SaveBackend;
  /** How many manual slots (default 3): keys `slot-1` … `slot-N`. */
  slots?: number;
  /** The autosave's key (default `auto`). */
  autosaveKey?: string;
  /** Minimum ms between autosaves; a call inside it is skipped unless forced (default 0). */
  autosaveMinIntervalMs?: number;
  compression?: Compression;
  /** Wall clock (default Date.now). */
  now?: () => number;
  /** Called after every write, with the key (a sync can follow it). */
  onWrite?: (key: string) => void;
}

/** What a write reports. */
export interface WriteResult extends RecordInfo {
  stats: EncodeStats;
  /** Encode time, ms. */
  encodeMs: number;
}

/** A slot or the autosave, as a list shows it. */
export interface SlotInfo {
  key: string;
  kind: "slot" | "auto";
  /** The slot's number (1…N), or 0 for the autosave. */
  index: number;
  record: RecordInfo | null;
}

/** A loaded save: the migrated document, how it got there, and the decode time. */
export interface LoadResult extends ReadResult {
  record: RecordInfo;
  decodeMs: number;
}

/** The store. */
export interface SaveStore {
  readonly backend: SaveBackend;
  /** The slot keys, then the autosave key. */
  keys(): string[];
  /** Write a save of `slices` under a slot key or the autosave key. */
  save(key: string, slices: Record<string, unknown>, meta?: SaveMeta): Promise<WriteResult>;
  /** Write the autosave, unless one was written less than `autosaveMinIntervalMs` ago (`force` ignores that). */
  autosave(slices: Record<string, unknown>, meta?: SaveMeta, o?: { force?: boolean }): Promise<WriteResult | null>;
  /** Read and migrate a save; null when the key is empty. Throws on a save it can't read. */
  load(key: string): Promise<LoadResult | null>;
  /** Read and migrate a value decoded elsewhere (a fixture file, an import). */
  read(raw: unknown): ReadResult;
  /** Every slot and the autosave, filled or not. */
  list(): Promise<SlotInfo[]>;
  delete(key: string): Promise<void>;
  /** Write a small named value (settings) as JSON. Its key must not be a slot's. */
  putValue(key: string, value: unknown, savedAt?: number): Promise<RecordInfo>;
  /** Read a named value, or null. */
  getValue<T>(key: string): Promise<{ value: T; record: RecordInfo } | null>;
}

/** Build a save store over a backend. */
export function createSaveStore(o: SaveStoreOptions): SaveStore {
  const slotCount = o.slots ?? 3;
  const auto = o.autosaveKey ?? "auto";
  const slotKeys = Array.from({ length: slotCount }, (_, i) => `slot-${i + 1}`);
  const saveKeys = [...slotKeys, auto];
  const now = o.now ?? (() => Date.now());
  const clock = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());
  let lastAutosave = -Infinity;

  const checkSaveKey = (key: string): void => {
    if (!saveKeys.includes(key)) throw new Error(`save store: "${key}" is not a save key (${saveKeys.join(", ")})`);
  };

  const write = async (key: string, slices: Record<string, unknown>, meta: SaveMeta = {}): Promise<WriteResult> => {
    const savedAt = now();
    const doc = o.schema.make(slices, meta, savedAt);
    const t0 = clock();
    const { bytes, stats } = await encodeSave(doc, { compression: o.compression });
    const encodeMs = clock() - t0;
    const record = { key, bytes, savedAt, rev: revOf(bytes), meta: doc.meta };
    await o.backend.put(record);
    o.onWrite?.(key);
    return { ...infoOf(record), stats, encodeMs };
  };

  return {
    backend: o.backend,
    keys: () => [...saveKeys],
    async save(key, slices, meta) {
      checkSaveKey(key);
      return write(key, slices, meta);
    },
    async autosave(slices, meta, opts = {}) {
      const t = clock();
      if (!opts.force && t - lastAutosave < (o.autosaveMinIntervalMs ?? 0)) return null;
      lastAutosave = t;
      return write(auto, slices, meta);
    },
    async load(key) {
      checkSaveKey(key);
      const record = await o.backend.get(key);
      if (!record) return null;
      const t0 = clock();
      const raw = await decodeSave(record.bytes);
      const read = o.schema.read(raw);
      return { ...read, record: infoOf(record), decodeMs: clock() - t0 };
    },
    read: (raw) => o.schema.read(raw),
    async list() {
      const records = new Map((await o.backend.list()).map((r) => [r.key, r]));
      return saveKeys.map((key) => ({
        key,
        kind: key === auto ? ("auto" as const) : ("slot" as const),
        index: key === auto ? 0 : slotKeys.indexOf(key) + 1,
        record: records.get(key) ?? null,
      }));
    },
    async delete(key) {
      checkSaveKey(key);
      return o.backend.delete(key);
    },
    async putValue(key, value, savedAt = now()) {
      if (saveKeys.includes(key)) throw new Error(`save store: "${key}" is a save key; values need their own`);
      const bytes = jsonBytes(value);
      const record = { key, bytes, savedAt, rev: revOf(bytes), meta: {} };
      await o.backend.put(record);
      o.onWrite?.(key);
      return infoOf(record);
    },
    async getValue<T>(key: string) {
      const record = await o.backend.get(key);
      return record ? { value: fromJsonBytes<T>(record.bytes), record: infoOf(record) } : null;
    },
  };
}
